import { DocsNav, DocsPage } from '../../models/docs';
import { docsAssetWorkerIsSessionKeyed } from '../../serviceWorker/registerDocsAssetCache';
import { callExternalApi, CallResult } from '../callExternalApi';

// GET /api/docs/nav — the navigation hierarchy rendered in the docs sidebar.
// `simulateRole` is a non-production docs role-simulation slug; when set it is
// forwarded so the API trims the tree as that role rather than the caller's.
export async function fetchDocsNav(simulateRole?: string, signal?: AbortSignal): Promise<CallResult<DocsNav>> {
  const query = simulateRole ? `?simulate_role=${encodeURIComponent(simulateRole)}` : '';
  return await callExternalApi<DocsNav>({
    url: `api/docs/nav${query}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    },
    signal
  });
}

// GET /api/docs/page?slug=<slug> — the Markdown body for a single docs page.
// `simulateRole` is forwarded as in fetchDocsNav so page access and in-page link
// redaction are evaluated as the simulated role.
export async function fetchDocsPage(
  slug: string,
  simulateRole?: string,
  signal?: AbortSignal
): Promise<CallResult<DocsPage>> {
  if (!slug) return { data: null, error: { message: 'Must provide a slug to fetch a docs page' } };

  const roleQuery = simulateRole ? `&simulate_role=${encodeURIComponent(simulateRole)}` : '';
  return await callExternalApi<DocsPage>({
    url: `api/docs/page?slug=${encodeURIComponent(slug)}${roleQuery}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    },
    signal
  });
}

const EXTERNAL_URL_PATTERN = /^(https?:|data:)/i;

// True when an image `src` points at an external/inline resource that the
// browser can load directly, rather than a docs asset that must be streamed
// through the authenticated asset endpoint.
export function isExternalDocsAssetUrl(src: string): boolean {
  return EXTERNAL_URL_PATTERN.test(src);
}

// Docs pages reference images as `[/]api/docs/assets/<path>`, the only form upstack_api
// serves (its docs lint refuses any other), while the asset endpoint expects the path
// relative to the docs `assets/` folder (`screenshots/foo.png`). This normalizes an
// authored `src` to that path. External URLs (http/https/data) are returned unchanged
// so they can be rendered directly.
export function resolveDocsAssetPath(src: string): string {
  if (EXTERNAL_URL_PATTERN.test(src)) return src;

  const withoutQuery = src.split(/[?#]/)[0];
  // Everything after the first `assets/` segment is the path the API serves.
  const match = /(?:^|\/)assets\/(.+)$/.exec(withoutQuery);
  if (match) return match[1];

  // No `assets/` anchor — drop any leading `./`, `../`, or `/` and serve the rest.
  return withoutQuery.replace(/^(?:\.\.?\/)+/, '').replace(/^\/+/, '');
}

// GET /api/docs/asset/*path — streams a docs image (png/jpg/jpeg/svg/webp) as a
// Blob. `path` is relative to the docs assets folder; each segment is encoded so
// spaces are safe while the slash hierarchy the glob route expects is preserved.
// `simulateRole` is forwarded as in fetchDocsNav, since the API serves an image
// only through a page the role it decides for can see.
export async function fetchDocsAsset(
  path: string,
  simulateRole?: string,
  signal?: AbortSignal
): Promise<CallResult<Blob>> {
  if (!path) return { data: null, error: { message: 'Must provide a path to fetch a docs asset' } };

  const encodedPath = path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const roleQuery = simulateRole ? `?simulate_role=${encodeURIComponent(simulateRole)}` : '';

  if (!(await docsAssetWorkerIsSessionKeyed())) {
    return { data: null, error: { message: 'Docs images are unavailable until the image cache keys them by session' } };
  }

  return await callExternalApi<Blob>({
    url: `api/docs/asset/${encodedPath}${roleQuery}`,
    method: 'GET',
    responseType: 'blob',
    signal
  });
}
